// Deployment trigger: Hero video auto-play feature
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Users, Search, Star, Play, X, ChevronLeft, ChevronRight, Tag, ArrowRight, Pause, Volume2, VolumeX, Maximize2, Images, Check, MapPin, Coffee, Wifi, Utensils, Waves, Tag as TagIcon, AlertCircle, ChevronsDown } from 'lucide-react';
import { format, addDays, isBefore, startOfToday } from 'date-fns';
import axios from 'axios';
import { trackEvent, trackBookNow, trackViewRoom, trackFunnelStep } from '../../utils/analytics';
import { toast } from 'sonner';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../components/ui/accordion';
import { cn } from '../../lib/utils';
import ImageGalleryOverlay from '../../components/ImageGalleryOverlay';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import BookingSearchModal from '../../components/BookingSearchModal';
import { getFinalPriceBreakdown, PRICE_EXCLUDES_TAX_LABEL, toPreTaxPrice } from '../../utils/pricing';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Video Modal Component for Room Tours
const VideoModal = ({ isOpen, onClose, videoUrl, roomName }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isOpen && videoRef.current) {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        setIsPlaying(false);
      });
    }
  }, [isOpen]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    const container = document.getElementById('home-video-modal-container');
    if (container) {
      if (!document.fullscreenElement) {
        container.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const prog = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(prog);
    }
  };

  const handleSeek = (e) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = percent * videoRef.current.duration;
    }
  };

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsPlaying(false);
    setProgress(0);
    onClose();
  };

  if (!isOpen) return null;

  const isYouTube = videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be');

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black"
        onClick={handleClose}
      >
        {/* Close Button - Fixed at top right */}
        <button
          onClick={handleClose}
          className="fixed top-4 right-4 z-[60] w-12 h-12 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors"
          data-testid="close-video-modal"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Room Name - Fixed at top left */}
        <div className="fixed top-4 left-4 z-[60]">
          <h3 className="text-white font-display text-lg drop-shadow-lg">
            Room Tour: {roomName}
          </h3>
        </div>

        <motion.div
          id="home-video-modal-container"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full h-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Video Container - Fullscreen */}
          <div className="relative bg-black w-full h-full flex items-center justify-center group">
            {isYouTube ? (
              <iframe
                src={videoUrl?.replace('watch?v=', 'embed/') + '?autoplay=1'}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`Room Tour: ${roomName}`}
              />
            ) : (
              <>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  className="w-full h-full object-contain"
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={() => setIsPlaying(false)}
                  playsInline
                  data-testid="room-tour-video"
                />

                {/* Video Controls Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <button
                    onClick={togglePlay}
                    className="w-20 h-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100"
                    data-testid="video-play-pause"
                  >
                    {isPlaying ? (
                      <Pause className="w-8 h-8" />
                    ) : (
                      <Play className="w-8 h-8 ml-1" />
                    )}
                  </button>
                </div>

                {/* Bottom Controls */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div
                    className="w-full h-1 bg-white/30 rounded-full cursor-pointer mb-3"
                    onClick={handleSeek}
                  >
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={togglePlay}
                        className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                      >
                        {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                      </button>
                      <button
                        onClick={toggleMute}
                        className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                      >
                        {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                      </button>
                    </div>
                    <button
                      onClick={toggleFullscreen}
                      className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                    >
                      <Maximize2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Home = () => {
  const [checkIn, setCheckIn] = useState(new Date());
  const [checkOut, setCheckOut] = useState(addDays(new Date(), 1));
  const [guests, setGuests] = useState(2);
  const [heroContent, setHeroContent] = useState({
    title: 'Spencer Green Hotel',
    subtitle: 'Hotel view pegunungan di Batu Malang untuk Liburan & Corporate Event',
    image: 'https://res.cloudinary.com/dgfjos8xa/image/upload/v1767362992/spencer-green-hotel-batu-malang-main-pool-view-building-night_u2ienp.webp'
  });
  const [promoBanner, setPromoBanner] = useState({
    title: 'Special Weekend Offer',
    description: 'Get 20% off for weekend stays. Use code: WEEKEND20',
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1200',
    is_active: true
  });
  const [reviews, setReviews] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [todaysRates, setTodaysRates] = useState({});
  const [availableRooms, setAvailableRooms] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [cart, setCart] = useState([]);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState({ url: '', name: '' });
  const [isSearching, setIsSearching] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    special_requests: '',
    promo_code: '',
    rate_plan_id: '',
    payment_method: '',
    payment_channel: ''
  });
  const [paymentChannels, setPaymentChannels] = useState([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
    const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const offersRef = useRef(null);
  const roomsRef = useRef(null);
  const bookingEngineRef = useRef(null);
  const [showMobileBooking, setShowMobileBooking] = useState(false);

  // Promo overlay state
  const [showPromoOverlay, setShowPromoOverlay] = useState(false);
  const [selectedPromoOffer, setSelectedPromoOffer] = useState(null);

  const faqs = [
    {
      question: "Apa Spencer Green Hotel cocok untuk liburan keluarga?",
      answer: "Ya, Spencer Green Hotel cocok untuk liburan keluarga karena memiliki fasilitas seperti kolam renang outdoor, restoran, Wi-Fi, area parkir, dan suasana hotel yang nyaman."
    },
    {
      question: "Di mana lokasi Spencer Green Hotel Batu?",
      answer: "Spencer Green Hotel berlokasi di Jl. Raya Punten No.86, Punten, Bumiaji, Kota Batu, Jawa Timur."
    },
    {
      question: "Apakah Spencer Green Hotel dekat Selecta?",
      answer: "Spencer Green Hotel berada di kawasan Batu yang memiliki akses mudah ke Selecta Recreation Park dan beberapa tempat wisata populer lainnya."
    },
    {
      question: "Apakah Spencer Green Hotel memiliki rooftop cafe?",
      answer: "Ya, Spencer Green Hotel memiliki rooftop cafe yang cocok untuk bersantai sambil menikmati suasana Batu dan view pegunungan."
    },
    {
      question: "Apa saja fasilitas Spencer Green Hotel?",
      answer: "Fasilitas yang tersedia antara lain kolam renang outdoor, restoran, rooftop cafe, Wi-Fi, parkir, resepsionis 24 jam, spa, room service, dan meeting/function hall."
    }
  ];

  const location = useLocation();
  const navigate = useNavigate();

  // Listen for #booking hash from header/external links
  useEffect(() => {
    if (location.hash === '#booking') {
      setShowSearchModal(true);
      // Clear hash so that clicking the link again works if modal was closed
      // Use navigate to keep React Router in sync
      const newPath = location.pathname;
      navigate(newPath, { replace: true });
    }
  }, [location.hash, navigate]);

  // Handle incoming search from other pages
  useEffect(() => {
    if (location.state?.doSearch && location.state?.searchParams) {
      const { checkIn: inDate, checkOut: outDate, guests: g, promoCode: code, verifiedPromo: vp } = location.state.searchParams;

      // Update local state
      if (inDate) setCheckIn(new Date(inDate));
      if (outDate) setCheckOut(new Date(outDate));
      if (g) setGuests(g);
      if (code) setPromoCode(code);
      if (vp) setVerifiedPromo(vp);

      // Trigger search
      searchAvailability({
        checkIn: new Date(inDate),
        checkOut: new Date(outDate)
      });

      // Clear state to prevent re-search on reload (optional, but good practice)
      // window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Gallery state

  const [showGallery, setShowGallery] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryRoomName, setGalleryRoomName] = useState('');

  const openGallery = (room, startIndex = 0) => {
    if (room.images && room.images.length > 0) {
      setGalleryImages(room.images);
      setGalleryIndex(startIndex);
      setGalleryRoomName(room.name);
      setShowGallery(true);
    }
  };

  // Dynamic offers from API
  const [offers, setOffers] = useState([]);

  // Promo Code State
  const [promoCode, setPromoCode] = useState('');
  const [verifiedPromo, setVerifiedPromo] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const [enablePaymentGateway, setEnablePaymentGateway] = useState(false);
  const [checkoutProvider, setCheckoutProvider] = useState('existing');

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/content/settings`);
      const bookingEngine = response.data.find(c => c.section === 'booking_engine');
      if (bookingEngine && bookingEngine.content) {
        const source = bookingEngine.content.inventory_source || 'legacy';
        setEnablePaymentGateway(bookingEngine.content.enable_payment_gateway || false);
        setCheckoutProvider(bookingEngine.content.checkout_provider || 'existing');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  useEffect(() => {
    fetchContent();
    fetchReviews();
    fetchRooms();
    fetchOffers();
    fetchTodaysRates();
    fetchPaymentChannels();
    fetchSettings();
    initData();
  }, []);


  const initData = async () => {
    try {
      await axios.post(`${API_URL}/init`);
    } catch (error) {
      // Already initialized
    }
  };

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/content/home`);
      const hero = response.data.find(c => c.section === 'hero');
      const promo = response.data.find(c => c.section === 'promo_banner');
      if (hero) setHeroContent(hero.content);
      if (promo) setPromoBanner(promo.content);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const fetchPaymentChannels = async () => {
    setIsLoadingChannels(true);
    try {
      const response = await axios.get(`${API_URL}/payment/channels`);
      setPaymentChannels(response.data);
    } catch (error) {
      console.error('Error fetching payment channels:', error);
    } finally {
      setIsLoadingChannels(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/reviews`);
      if (response.data && response.data.length > 0) {
        setReviews(response.data);
      } else {
        // Fallback dummy reviews
        setReviews([
          {
            guest_name: "Budi Santoso",
            rating: 5,
            comment: "Pelayanan sangat memuaskan, kamar bersih dan pemandangan indah. Sangat recommended untuk liburan keluarga."
          },
          {
            guest_name: "Siti Aminah",
            rating: 5,
            comment: "Makanan enak, lokasi strategis dekat tempat wisata. Staff ramah dan sangat membantu."
          },
          {
            guest_name: "John Doe",
            rating: 4,
            comment: "Great view and friendly staff. Will definitely come back again when visiting Batu."
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API_URL}/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API_URL}/special-offers`);
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  const fetchTodaysRates = async () => {
    try {
      const today = new Date();
      const tomorrow = addDays(today, 1);
      const params = {
        check_in: format(today, 'yyyy-MM-dd'),
        check_out: format(tomorrow, 'yyyy-MM-dd'),
        adults: 2
      };

      console.log('Home: Fetching today rates params:', params);

      const response = await axios.get(`${API_URL}/availability`, { params });

      console.log('Home: Availability response:', response.data);

      const rates = {};
      response.data.forEach(room => {
        rates[String(room.room_type_id)] = room.available_rate || room.base_price;
      });
      setTodaysRates(rates);
    } catch (error) {
      console.error('Error fetching today rates:', error);
    }
  };

  const searchAvailability = async (searchData = null) => {
    setIsSearching(true);

    // Use searchData if provided, otherwise fall back to state
    const params = {
      check_in: format(searchData?.checkIn || checkIn, 'yyyy-MM-dd'),
      check_out: format(searchData?.checkOut || checkOut, 'yyyy-MM-dd'),
      adults: searchData?.guests || guests,
      promocode: searchData?.promoCode || promoCode || undefined
    };

    try {
      // Pass verification data if exists (for price consistency)
      const response = await axios.get(`${API_URL}/availability`, { params });
      setAvailableRooms(response.data);
      setShowAvailability(true);

      // Scroll to rooms section with offset for header
      setTimeout(() => {
        if (roomsRef.current) {
          const headerOffset = 100;
          const elementPosition = roomsRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        }
      }, 100);
    } catch (error) {
      toast.error('Error searching availability');
    } finally {
      setIsSearching(false);
    }
  };

  const verifyPromoCode = async () => {
    if (!promoCode) return;
    setIsVerifying(true);
    try {
      const response = await axios.post(`${API_URL}/promo/verify`, {
        code: promoCode,
        check_in: format(checkIn, 'yyyy-MM-dd')
      });

      if (response.data.valid) {
        setVerifiedPromo(response.data);
        toast.success(response.data.message || 'Kode promo berhasil digunakan!');
      }
    } catch (error) {
      setVerifiedPromo(null);
      toast.error(error.response?.data?.detail || 'Kode promo tidak valid');
    } finally {
      setIsVerifying(false);
    }
  };

  const promoAppliesToRoom = (room) => verifiedPromo && (
    !verifiedPromo.room_type_ids?.length || verifiedPromo.room_type_ids.includes(room.room_type_id)
  );

  const getDiscountedPrice = (room, originalPrice) => {
    if (!promoAppliesToRoom(room)) return originalPrice;

    if (verifiedPromo.discount_type === 'percent') {
      return originalPrice * (1 - verifiedPromo.discount_value / 100);
    } else if (verifiedPromo.discount_type === 'fixed') {
      return Math.max(0, originalPrice - verifiedPromo.discount_value);
    }
    return Math.min(originalPrice, verifiedPromo.discount_value);
  };

  const verifyBookingPromoCode = async () => {
    if (!bookingForm.promo_code) {
      setVerifiedPromo(null);
      return;
    }
    setIsVerifying(true);
    try {
      const response = await axios.post(`${API_URL}/promo/verify`, {
        code: bookingForm.promo_code,
        check_in: format(checkIn, 'yyyy-MM-dd'),
        room_type_ids: cart.map(c => c.room_type_id)
      });

      if (response.data.valid) {
        setVerifiedPromo(response.data);
        const hasApplicableRoom = !response.data.room_type_ids?.length || 
          cart.some(c => response.data.room_type_ids.includes(c.room_type_id));
        if (cart.length > 0 && !hasApplicableRoom) {
          toast.warning('Kode promo aktif, namun tidak berlaku untuk kamar yang dipilih');
        } else {
          toast.success(response.data.message || 'Kode promo berhasil digunakan!');
        }
      }
    } catch (error) {
      setVerifiedPromo(null);
      toast.error(error.response?.data?.detail || 'Kode promo tidak valid');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCheckInSelect = (date) => {
    setCheckIn(date);
    // Always default check-out to H+1 when check-in changes
    setCheckOut(addDays(date, 1));
  };

  const handleBookRoom = (room) => {
    trackFunnelStep('guest_info', { room_name: room.name });

    if (room.checkout_provider === 'siteminder') {
      if (!room.external_booking_url) {
        toast.error('Booking engine SiteMinder belum tersedia');
        return;
      }
      window.open(room.external_booking_url, '_blank', 'noopener,noreferrer');
      toast.info('Lanjutkan checkout aman di halaman resmi SiteMinder');
      return;
    }

    const defaultPlanId = room.rate_plans && room.rate_plans.length > 0
      ? room.rate_plans[0].rate_plan_id
      : 'standard';

    setCart(prevCart => {
      if (prevCart.some(item => item.room_type_id === room.room_type_id)) {
        return prevCart;
      }
      return [...prevCart, {
        ...room,
        rate_plan_id: defaultPlanId,
        num_rooms: 1,
        max_allotment: room.min_allotment ?? room.allotment ?? 10
      }];
    });

    setBookingForm(prev => ({
      ...prev,
      promo_code: verifiedPromo ? verifiedPromo.code : prev.promo_code
    }));
    setShowBookingModal(true);
  };

  const handlePlayVideo = (room) => {
    const videoUrl = room.video_url || 'https://sample-videos.com/video321/mp4/720/big_buck_bunny_720p_1mb.mp4';
    setSelectedVideo({ url: videoUrl, name: room.name });
    setShowVideoModal(true);
  };



  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    guest_name: '',
    guest_email: '',
    rating: 5,
    comment: ''
  });

  // State for room image carousel
  const [imageIndices, setImageIndices] = useState({});

  const submitReview = async () => {
    if (!reviewForm.guest_name || !reviewForm.guest_email || !reviewForm.comment) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/reviews`, {
        ...reviewForm,
        reservation_id: '' // Optional
      });
      toast.success('Review submitted successfully! Pending approval.');
      setShowReviewModal(false);
      setReviewForm({ guest_name: '', guest_email: '', rating: 5, comment: '' });
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  const submitBooking = async () => {
    if (!bookingForm.guest_name || !bookingForm.guest_email || !bookingForm.guest_phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsBooking(true);
    trackFunnelStep('payment', { room_name: cart.map(c => c.name).join(', ') }); // Intent to pay (redirect to WA)

    try {
      const response = await axios.post(`${API_URL}/reservations`, {
        ...bookingForm,
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        guests,
        rooms: cart.map(c => ({
          room_type_id: c.room_type_id,
          num_rooms: c.num_rooms,
          rate_plan_id: c.rate_plan_id
        }))
      });

      trackFunnelStep('success', { room_name: cart.map(c => c.name).join(', '), booking_code: response.data.booking_code });
      toast.success('Booking successful! Check your email for confirmation.');
      setShowBookingModal(false);
      setBookingForm({ guest_name: '', guest_email: '', guest_phone: '', special_requests: '', promo_code: '', rate_plan_id: '' });

      if (response.data.payment_url) {
        const paymentUrl = response.data.payment_url;
        const isSandbox = paymentUrl.includes('sandbox');
        const scriptUrl = isSandbox 
          ? 'https://sandbox.doku.com/jokul-checkout-js/v1/jokul-checkout-1.0.0.js'
          : 'https://jokul.doku.com/jokul-checkout-js/v1/jokul-checkout-1.0.0.js';
        
        if (window.loadJokulCheckout) {
          window.loadJokulCheckout(paymentUrl);
        } else {
          const script = document.createElement('script');
          script.src = scriptUrl;
          script.async = true;
          script.onload = () => {
            if (window.loadJokulCheckout) {
              window.loadJokulCheckout(paymentUrl);
            }
          };
          document.body.appendChild(script);
        }
      } else {
        // Old flow (WhatsApp)
        const waNumber = '6281130700206';
        const roomsSummary = cart.map(item => `${item.name} x${item.num_rooms}`).join(', ');
        const message = `Halo Spencer Green Hotel, saya baru saja melakukan booking via website.\n\nBooking Code: ${response.data.booking_code}\nNama: ${response.data.guest_name}\nKamar: ${roomsSummary}\nCheck-in: ${response.data.check_in}`;
        window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`, '_blank');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally {
      setIsBooking(false);
    }
  };

  const nextReview = () => {
    setCurrentReviewIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentReviewIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const scrollOffers = (direction) => {
    if (offersRef.current) {
      const scrollAmount = 320;
      offersRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section className="relative h-[85vh] sm:h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image/Video */}
        <div className="absolute inset-0 z-0">
          {heroContent?.video ? (
            // Video background - auto-play, muted, loop
            (() => {
              const isYouTube = heroContent.video.includes('youtube.com') || heroContent.video.includes('youtu.be');
              if (isYouTube) {
                // Extract YouTube video ID
                let videoId = '';
                if (heroContent.video.includes('youtu.be/')) {
                  videoId = heroContent.video.split('youtu.be/')[1]?.split(/[?&#]/)[0];
                } else {
                  const urlParams = new URLSearchParams(heroContent.video.split('?')[1]);
                  videoId = urlParams.get('v');
                }
                return (
                  <div className="w-full h-full overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto"
                      style={{ aspectRatio: '16/9', pointerEvents: 'none' }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Hero Video"
                      frameBorder="0"
                    />
                  </div>
                );
              }
              // Direct video file (.mp4, .webm, etc.)
              return (
                <video
                  key={heroContent.video}
                  src={heroContent.video}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className="w-full h-full object-cover"
                  poster={heroContent?.image || undefined}
                />
              );
            })()
          ) : (
            <img
              src={heroContent?.image || "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"}
              alt="Luxury Hotel"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative z-20 text-center px-4 max-w-5xl mx-auto -mt-20 sm:mt-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-emerald-300 uppercase tracking-[0.2em] sm:tracking-[0.3em] text-xs sm:text-sm mb-3 sm:mb-4">Hotel dengan View Pegunungan dekat Wisata Selecta</p>
            {/* SEO H1 - Hidden but indexable */}
            <h1 className="sr-only">SPENCER GREEN HOTEL BATU - Hotel di Batu Malang dengan Kamar Nyaman, Kolam Renang & Paket Meeting</h1>
            {/* Visual Title */}
            <p className="font-display hero-title text-4xl sm:text-5xl text-white mb-4 sm:mb-6" role="heading" aria-level="2">
              {heroContent?.title}
            </p>
            <p className="hero-subtitle text-base sm:text-lg lg:text-xl text-emerald-100 max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
              {heroContent?.subtitle}
            </p>
            <Button
              onClick={() => setShowSearchModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6 rounded-full shadow-lg transition-transform hover:scale-105"
            >
              Cek Harga & Ketersediaan
            </Button>
          </motion.div>
        </div>

        {/* Scroll Indicator - Bottom Center */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 cursor-pointer text-white/70 hover:text-white"
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            y: [0, 8, 0]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          whileHover={{
            y: 12,
            scale: 1.1,
            transition: { duration: 0.2 }
          }}
          onClick={() => {
            const searchSection = document.getElementById('search-modal-trigger') || document.body;
            // Scroll down slightly or to next section
            window.scrollTo({
              top: window.innerHeight - 80,
              behavior: 'smooth'
            });
          }}
        >
          <div className="flex flex-col items-center gap-1">
            <span className="text-[10px] uppercase tracking-widest font-light opacity-0 hover:opacity-100 transition-opacity">Scroll</span>
            <ChevronsDown className="w-8 h-8 stroke-[1px]" />
          </div>
        </motion.div>

        {/* Desktop Search Modal */}
        <BookingSearchModal
          isOpen={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          onSearch={(data) => {
            // Update local state with data from modal
            setCheckIn(data.checkIn);
            setCheckOut(data.checkOut);
            setGuests(data.guests);
            setPromoCode(data.promoCode || '');
            setVerifiedPromo(data.verifiedPromo);

            // Trigger search with valid data
            searchAvailability(data);
            setShowSearchModal(false);
          }}
          initialCheckIn={checkIn}
          initialCheckOut={checkOut}
          initialGuests={guests}
          useExternalPromo={checkoutProvider === 'siteminder'}
        />
      </section>

      {/* Special Offers Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white" data-testid="offers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-emerald-600 uppercase tracking-widest text-xs sm:text-sm mb-1 sm:mb-2">Limited Time</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium text-gray-900">Special Offers</h2>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => scrollOffers('left')}
                className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center hover:bg-emerald-200 transition-colors"
                data-testid="offers-scroll-left"
              >
                <ChevronLeft className="w-5 h-5 text-emerald-700" />
              </button>
              <button
                onClick={() => scrollOffers('right')}
                className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center hover:bg-emerald-200 transition-colors"
                data-testid="offers-scroll-right"
              >
                <ChevronRight className="w-5 h-5 text-emerald-700" />
              </button>
            </div>
          </div>

          <div
            ref={offersRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {offers.map((offer, index) => (
              <motion.div
                key={offer.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`flex-shrink-0 w-[280px] sm:w-[300px] bg-white rounded-xl overflow-hidden shadow-soft border border-gray-100 snap-start${offer.promo_media_url ? ' cursor-pointer group/offer hover:shadow-lg transition-shadow' : ''}`}
                data-testid={`offer-card-${offer.id}`}
                onClick={() => {
                  if (offer.promo_media_url) {
                    setSelectedPromoOffer(offer);
                    setShowPromoOverlay(true);
                  }
                }}
              >
                <div className="relative h-40 overflow-hidden">
                  <img src={offer.image} alt={offer.title} className={`w-full h-full object-cover${offer.promo_media_url ? ' transition-transform duration-300 group-hover/offer:scale-105' : ''}`} />
                  <div className="absolute top-3 left-3">
                    <span className="bg-emerald-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                      {offer.code}
                    </span>
                  </div>
                  {offer.promo_media_url && (
                    <div className="absolute inset-0 bg-black/0 group-hover/offer:bg-black/20 transition-colors flex items-center justify-center">
                      <div className="opacity-0 group-hover/offer:opacity-100 transition-opacity bg-white/90 rounded-full p-3 shadow-lg">
                        <Search className="w-5 h-5 text-emerald-700" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-display text-lg font-semibold text-gray-900 mb-2">{offer.title}</h3>
                  <p className="text-gray-500 text-sm mb-3 line-clamp-2">{offer.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Valid until {offer.validUntil}</span>
                    {offer.promo_media_url ? (
                      <span className="text-emerald-600 text-sm font-medium flex items-center">
                        Lihat Promo <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowSearchModal(true); }}
                        className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center"
                      >
                        Book <ArrowRight className="w-4 h-4 ml-1" />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Rooms Section */}
      {
        showAvailability && (
          <section ref={roomsRef} className="py-16 sm:py-20 bg-emerald-50/50" data-testid="available-rooms-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10 sm:mb-12">
                <p className="text-emerald-600 uppercase tracking-widest text-xs sm:text-sm mb-2">Available Rooms</p>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium text-gray-900">
                  {format(checkIn, 'dd MMM')} - {format(checkOut, 'dd MMM yyyy')}
                </h2>
                <button
                  onClick={() => setShowSearchModal(true)}
                  className="text-emerald-600 hover:text-emerald-700 font-medium text-sm mt-2 flex items-center justify-center mx-auto gap-1"
                >
                  <Calendar className="w-4 h-4" /> Change Dates
                </button>
                {availableRooms[0]?.booking_source === 'siteminder' && (
                  <div className="inline-flex mt-4 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
                    Harga & ketersediaan
                  </div>
                )}
              </div>

              {availableRooms.length === 0 ? (
                <p className="text-center text-gray-500">No rooms available for selected dates.</p>
              ) : (
                <div className="space-y-6">
                  {availableRooms.map((room, index) => (
                    <motion.div
                      key={room.room_type_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-xl overflow-hidden shadow-soft"
                      data-testid={`room-card-${room.room_type_id}`}
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-3">
                        <div className="relative h-64 lg:h-auto overflow-hidden group cursor-pointer" onClick={() => openGallery(room, 0)}>
                          <img
                            src={room.images?.[0] || 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800'}
                            alt={room.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          {/* Photo count badge */}
                          {room.images && room.images.length > 1 && (
                            <div className="absolute top-4 left-4 bg-black/50 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 backdrop-blur-sm">
                              <Images className="w-4 h-4" />
                              {room.images.length} Foto
                            </div>
                          )}
                          {/* Room Tour Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePlayVideo(room);
                            }}
                            data-testid={`room-tour-btn-${room.room_type_id}`}
                            className="absolute bottom-4 right-4 flex items-center gap-2 bg-white/90 hover:bg-white text-emerald-700 px-4 py-2 rounded-full transition-all shadow-lg hover:shadow-xl"
                          >
                            <Play className="w-5 h-5" />
                            <span className="font-medium text-sm">Room Tour</span>
                          </button>
                        </div>
                        <div className="lg:col-span-2 p-6 lg:p-8 flex flex-col justify-between">
                          <div>
                            <h3 className="font-display text-2xl font-semibold text-gray-900 mb-3">{room.name}</h3>
                            <p className="text-gray-500 mb-4 line-clamp-2">{room.description}</p>
                            <div className="flex flex-wrap gap-2 mb-4">
                              {room.amenities?.slice(0, 5).map((amenity, i) => (
                                <span key={i} className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                                  {amenity}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-100">
                            <div className="flex flex-col">
                              {room.min_allotment !== undefined ? (
                                <span className="text-sm text-gray-500 mb-0.5">Rate Malam ini</span>
                              ) : (
                                <span className="text-sm text-gray-500 mb-0.5">Mulai dari</span>
                              )}

                              {promoAppliesToRoom(room) ? (
                                <>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-400 line-through">
                                      Rp {toPreTaxPrice(room.available_rate || room.base_price).toLocaleString('id-ID')}
                                    </span>
                                    <span className="text-emerald-600 text-xs font-semibold bg-emerald-50 px-2 py-0.5 rounded">
                                      {verifiedPromo.discount_type === 'manual_price'
                                        ? 'Harga promo'
                                        : `Hemat ${verifiedPromo.discount_type === 'percent' ? `${verifiedPromo.discount_value}%` : `Rp ${verifiedPromo.discount_value.toLocaleString('id-ID')}`}`}
                                    </span>
                                  </div>
                                  <span className="text-emerald-600 font-bold text-2xl">
                                    Rp {toPreTaxPrice(getDiscountedPrice(room, room.available_rate || room.base_price)).toLocaleString('id-ID')}
                                  </span>
                                </>
                              ) : (
                                <span className="text-emerald-600 font-bold text-2xl">
                                  Rp {toPreTaxPrice(room.available_rate || room.base_price).toLocaleString('id-ID')}
                                </span>
                              )}
                              <p className="text-[10px] text-gray-400 mt-1">
                                {PRICE_EXCLUDES_TAX_LABEL}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              {room.min_allotment !== undefined && room.min_allotment > 0 && (
                                <span className="text-red-600 font-semibold text-sm animate-pulse">
                                  Sisa {room.min_allotment} kamar!
                                </span>
                              )}
                              <Button
                                onClick={() => handleBookRoom(room)}
                                data-testid={`book-room-btn-${room.room_type_id}`}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                              >
                                {room.checkout_provider === 'siteminder' ? 'Checkout SiteMinder' : 'Cek Harga & Ketersediaan'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )
      }

      {/* Promo Banner */}
      {
        promoBanner?.is_active && (
          <section className="py-12 sm:py-16 lg:py-20 bg-white" data-testid="promo-banner">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src={promoBanner.image}
                  alt="Promo"
                  className="w-full h-56 sm:h-64 md:h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-transparent flex items-center">
                  <div className="p-6 sm:p-8 md:p-12 max-w-lg">
                    <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-medium text-white mb-3 sm:mb-4">
                      {promoBanner.title}
                    </h3>
                    <p className="text-emerald-100 text-sm sm:text-base mb-4 sm:mb-6">{promoBanner.description}</p>
                    <Button onClick={() => setShowSearchModal(true)} className="bg-white text-emerald-800 hover:bg-emerald-100">
                      Cek Harga & Ketersediaan
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )
      }

      {/* Room Types Preview - Kempinski Style Full Width */}
      <section className="py-12 sm:py-16 lg:py-20 bg-emerald-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 sm:mb-12">
          <div className="text-center">
            <p className="text-emerald-600 uppercase tracking-widest text-xs sm:text-sm mb-2">Akomodasi</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Pilihan Kamar Nyaman Kami</h2>
          </div>
        </div>

        <div className="space-y-0">
          {rooms.map((room, index) => {
            // State logic for this iteration must be handled at top level or component level.
            // Since we are inside map in return, we can't call hooks here.
            // We need to use the state from the parent component.
            // See 'imageIndices' state usage below.
            const currentImageIndex = imageIndices[room.room_type_id] || 0;
            const images = room.images && room.images.length > 0 ? room.images : ['https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800'];

            return (
              <motion.div
                key={room.room_type_id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                onViewportLeave={() => {
                  // Reset to 0 when scrolling out of view
                  setImageIndices(prev => ({ ...prev, [room.room_type_id]: 0 }));
                }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true, margin: "-10%" }}
                // Changed viewport once to false? No, user said "when scroll past... reset". 
                // "once: true" prevents onViewportLeave? Actually `once: true` means animation executes once.
                // Events like onViewportLeave might still fire?
                // Documentation: if once:true, it maintains state. 
                // I should probably remove `once: true` or verify.
                // Actually, simpler: Use standard onViewportLeave prop, it works with whileInView.
                // But if I want it to reset EVERY time it leaves, `once: true` might block it?
                // Let's remove `viewport={{ once: true }}` to allow repeated observation?
                // The animation `whileInView` handles entrance. 
                // Let's rely on standard behavior.
                className={`relative ${index % 2 === 0 ? '' : ''}`}
              >
                <div className={`grid grid-cols-1 lg:grid-cols-2 ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                  <div className={`relative h-[40vh] min-h-[350px] overflow-hidden ${index % 2 === 1 ? 'lg:col-start-2' : ''} group`}>
                    <img
                      src={images[currentImageIndex]}
                      alt={room.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />

                    {/* Navigation Chevrons - Only if multiple images */}
                    {images.length > 1 && (
                      <>
                        <button
                          className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-white transition-opacity opacity-0 group-hover:opacity-100 mix-blend-difference"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImageIndices(prev => ({
                              ...prev,
                              [room.room_type_id]: (currentImageIndex - 1 + images.length) % images.length
                            }));
                          }}
                        >
                          <ChevronLeft className="w-8 h-8 stroke-[1px]" />
                        </button>
                        <button
                          className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-white transition-opacity opacity-0 group-hover:opacity-100 mix-blend-difference"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImageIndices(prev => ({
                              ...prev,
                              [room.room_type_id]: (currentImageIndex + 1) % images.length
                            }));
                          }}
                        >
                          <ChevronRight className="w-8 h-8 stroke-[1px]" />
                        </button>

                        {/* Slide Indicator Dots (Optional but helpful) */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          {images.map((_, idx) => (
                            <div
                              key={idx}
                              className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50'}`}
                            />
                          ))}
                        </div>
                      </>
                    )}

                    {/* Room Tour Button */}
                    <button
                      onClick={() => {
                        trackEvent('Content', 'play_video', room.name);
                        handlePlayVideo(room);
                      }}
                      data-testid={`home-room-tour-btn-${room.room_type_id}`}
                      className="absolute bottom-6 right-6 flex items-center gap-2 bg-white/90 hover:bg-white text-emerald-700 px-5 py-3 rounded-full transition-all shadow-lg hover:shadow-xl"
                    >
                      <Play className="w-5 h-5" />
                      <span className="font-medium">Room Tour</span>
                    </button>
                  </div>
                  <div className={`flex items-center bg-white ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                    <div className="p-8 sm:p-10 lg:p-16 max-w-xl">
                      <p className="text-emerald-600 uppercase tracking-widest text-xs mb-3">
                        {todaysRates[String(room.room_type_id)]
                          ? `Rate Malam ini Rp ${toPreTaxPrice(todaysRates[String(room.room_type_id)]).toLocaleString('id-ID')}`
                          : `Starting from Rp ${toPreTaxPrice(room.base_price)?.toLocaleString('id-ID')} / night`
                        }
                      </p>
                      <p className="text-[10px] text-gray-400 mt-[-8px] mb-4">
                        {PRICE_EXCLUDES_TAX_LABEL}
                      </p>
                      <h3 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 mb-4">{room.name}</h3>
                      <p className="text-gray-500 leading-relaxed mb-6">{room.description}</p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {room.amenities?.slice(0, 4).map((amenity, i) => (
                          <span key={i} className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                            {amenity}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-3">
                        {room.allotment !== undefined && (
                          <span className="text-red-600 font-semibold text-sm animate-pulse">
                            Sisa {room.allotment} kamar!
                          </span>
                        )}
                        <Button
                          onClick={() => {
                            trackBookNow(room.name);
                            setShowSearchModal(true);
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                        >
                          Cek Harga & Ketersediaan
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Instagram Feed Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white" data-testid="instagram-feed-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="elfsight-app-13b7556d-818e-4727-bf7b-176d65867e1d" data-elfsight-app-lazy></div>
        </div>
      </section>

      {/* Reviews Section */}
      {
        reviews.length > 0 && (
          <section className="py-12 sm:py-16 lg:py-20 bg-white" data-testid="reviews-section">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10 sm:mb-12">
                <p className="text-emerald-600 uppercase tracking-widest text-xs sm:text-sm mb-2">Testimoni</p>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Pengalaman Menginap Tamu Kami</h2>
                <Button
                  variant="outline"
                  onClick={() => setShowReviewModal(true)}
                  className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                >
                  Pengalaman Menginap Anda
                </Button>
              </div>

              <div className="relative">
                <div className="bg-emerald-50/50 rounded-2xl p-6 sm:p-8 md:p-12">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${i < reviews[currentReviewIndex]?.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  <p className="text-gray-600 text-base sm:text-lg italic mb-6">
                    &ldquo;{reviews[currentReviewIndex]?.comment}&rdquo;
                  </p>
                  <p className="font-semibold text-gray-900">{reviews[currentReviewIndex]?.guest_name}</p>
                </div>

                {reviews.length > 1 && (
                  <div className="flex justify-center mt-6 space-x-4">
                    <button
                      onClick={prevReview}
                      className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-emerald-50 transition-colors"
                      data-testid="prev-review-btn"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      onClick={nextReview}
                      className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-emerald-50 transition-colors"
                      data-testid="next-review-btn"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )
      }

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50 border-t" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <p className="text-emerald-600 uppercase tracking-widest text-xs sm:text-sm mb-2">Informasi Penting</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="w-full bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className={index === faqs.length - 1 ? "border-b-0" : ""}>
                <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-emerald-600 sm:text-lg">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed pt-2 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Complete Your Booking</DialogTitle>
          </DialogHeader>

          {cart.length > 0 && (
            <div className="space-y-6">
              
              {/* CART ITEMS LOOP */}
              {cart.map((item, index) => {
                const selectedPlan = item.rate_plans?.find(p => p.rate_plan_id === item.rate_plan_id)
                  || item.rate_plans?.[0]
                  || { nightly_price: item.available_rate || item.base_price };
                  
                return (
                  <div key={item.room_type_id} className="border border-emerald-100 rounded-lg p-4 relative">
                    <button 
                      onClick={() => {
                        setCart(prev => {
                          const newCart = prev.filter(c => c.room_type_id !== item.room_type_id);
                          if (newCart.length === 0) setShowBookingModal(false);
                          return newCart;
                        });
                      }}
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Hapus kamar"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                    
                    <div className="flex items-center space-x-4 mb-4">
                      <img
                        src={item.images?.[0] || 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=200'}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{item.name}</h4>
                        <p className="text-xs text-gray-500 mb-1">
                          {format(checkIn, 'dd MMM')} - {format(checkOut, 'dd MMM yyyy')}
                        </p>
                      </div>
                    </div>
                    
                    {item.rate_plans && item.rate_plans.length > 1 && (
                      <div className="mb-3">
                        <select
                          value={item.rate_plan_id}
                          onChange={(e) => {
                            setCart(prev => prev.map(c => 
                              c.room_type_id === item.room_type_id ? { ...c, rate_plan_id: e.target.value } : c
                            ));
                          }}
                          className="text-xs p-1.5 border rounded-lg bg-white w-full"
                        >
                          {item.rate_plans.map(plan => (
                            <option key={plan.rate_plan_id} value={plan.rate_plan_id}>
                              {plan.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg mt-2">
                      <div>
                        <Label className="text-xs font-medium text-gray-700">Jumlah</Label>
                        <p className="text-[10px] text-gray-500">Maks: {item.max_allotment} kamar</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setCart(prev => prev.map(c => c.room_type_id === item.room_type_id ? { ...c, num_rooms: Math.max(1, c.num_rooms - 1) } : c))}
                          disabled={item.num_rooms <= 1}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          −
                        </button>
                        <span className="text-sm font-semibold text-gray-900 w-4 text-center">{item.num_rooms}</span>
                        <button
                          type="button"
                          onClick={() => setCart(prev => prev.map(c => c.room_type_id === item.room_type_id ? { ...c, num_rooms: Math.min(c.max_allotment, c.num_rooms + 1) } : c))}
                          disabled={item.num_rooms >= item.max_allotment}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              <Button 
                variant="outline" 
                onClick={() => setShowBookingModal(false)}
                className="w-full border-dashed border-2 border-emerald-300 text-emerald-600 hover:bg-emerald-50 bg-white h-10"
              >
                + Tambah Kamar Lain
              </Button>
              
              {/* GRAND TOTAL SUMMARY */}
              {(() => {
                const subtotal = cart.reduce((sum, item) => {
                  const selectedPlan = item.rate_plans?.find(p => p.rate_plan_id === item.rate_plan_id) || item.rate_plans?.[0] || { nightly_price: item.available_rate || item.base_price };
                  const nights = Math.max(1, Math.round((checkOut - checkIn) / 86400000));
                  const roomTotal = selectedPlan.total_price ?? (selectedPlan.nightly_price * nights);
                  return sum + (roomTotal * item.num_rooms);
                }, 0);
                
                let discount = 0;
                if (verifiedPromo) {
                  let validForCart = true;
                  if (verifiedPromo.room_type_ids && verifiedPromo.room_type_ids.length > 0) {
                    validForCart = cart.some(item => verifiedPromo.room_type_ids.includes(item.room_type_id));
                  }
                  if (validForCart) {
                    if (verifiedPromo.discount_type === 'percent') {
                      discount = subtotal * (verifiedPromo.discount_value / 100);
                    } else if (verifiedPromo.discount_type === 'fixed') {
                      discount = verifiedPromo.discount_value;
                    } else if (verifiedPromo.discount_type === 'manual_price') {
                      const nights = Math.max(1, Math.round((checkOut - checkIn) / 86400000));
                      discount = cart.reduce((total, item) => {
                        if (verifiedPromo.room_type_ids?.length > 0 && !verifiedPromo.room_type_ids.includes(item.room_type_id)) {
                          return total;
                        }
                        const selectedPlan = item.rate_plans?.find(plan => plan.rate_plan_id === item.rate_plan_id) || item.rate_plans?.[0] || { nightly_price: item.available_rate || item.base_price };
                        const currentTotal = (selectedPlan.total_price ?? selectedPlan.nightly_price * nights) * item.num_rooms;
                        const manualTotal = verifiedPromo.discount_value * nights * item.num_rooms;
                        return total + Math.max(0, currentTotal - manualTotal);
                      }, 0);
                    }
                  }
                }
                
                discount = Math.min(subtotal, Math.max(0, discount));
                const grandTotal = Math.max(0, subtotal - discount);
                const breakdown = getFinalPriceBreakdown(subtotal);
                
                return (
                  <div className="bg-white border rounded-lg p-3 space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between items-center">
                      <span>Total Harga Dasar</span>
                      <span>Rp {breakdown.basePrice.toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Total Service Charge (10%)</span>
                      <span>Rp {breakdown.serviceCharge.toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b">
                      <span>Total Pajak Hotel (10%)</span>
                      <span>Rp {breakdown.tax.toLocaleString('id-ID')}</span>
                    </div>
                    {verifiedPromo && discount > 0 && (
                      <div className="flex justify-between items-center text-emerald-600 font-medium pb-2 border-b">
                        <span>Promo {verifiedPromo.code}</span>
                        <span>- Rp {discount.toLocaleString('id-ID')}</span>
                      </div>
                    )}
                    {verifiedPromo && discount === 0 && (
                      <div className="text-xs text-amber-600 pb-2 border-b">
                        Kode promo {verifiedPromo.code} aktif, namun tidak memotong harga untuk kamar ini.
                      </div>
                    )}
                    <div className="flex justify-between items-center font-bold text-gray-900 pt-1">
                      <span>Grand Total Pembayaran</span>
                      <span className="text-emerald-600 text-lg">Rp {grandTotal.toLocaleString('id-ID')}</span>
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-semibold text-gray-900">Data Pemesan</h4>
                <div>
                  <Label htmlFor="guest_name">Full Name *</Label>
                  <Input
                    id="guest_name"
                    data-testid="booking-guest-name"
                    value={bookingForm.guest_name}
                    onChange={(e) => setBookingForm({ ...bookingForm, guest_name: e.target.value })}
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <Label htmlFor="guest_email">Email *</Label>
                  <Input
                    id="guest_email"
                    type="email"
                    data-testid="booking-guest-email"
                    value={bookingForm.guest_email}
                    onChange={(e) => setBookingForm({ ...bookingForm, guest_email: e.target.value })}
                    placeholder="Enter your email"
                  />
                </div>
                <div>
                  <Label htmlFor="guest_phone">Phone *</Label>
                  <Input
                    id="guest_phone"
                    data-testid="booking-guest-phone"
                    value={bookingForm.guest_phone}
                    onChange={(e) => setBookingForm({ ...bookingForm, guest_phone: e.target.value })}
                    placeholder="Enter your phone number"
                  />
                </div>
                <div>
                  <Label htmlFor="promo_code">Promo Code</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="promo_code"
                      data-testid="booking-promo-code"
                      value={bookingForm.promo_code}
                      onChange={(e) => setBookingForm({ ...bookingForm, promo_code: e.target.value })}
                      placeholder="Enter promo code (optional)"
                      className="flex-1"
                    />
                    <Button 
                      type="button" 
                      onClick={verifyBookingPromoCode} 
                      disabled={isVerifying || !bookingForm.promo_code}
                      className="bg-gray-800 hover:bg-gray-700 text-white"
                    >
                      {isVerifying ? '...' : 'Verify'}
                    </Button>
                  </div>
                  {verifiedPromo && (
                    <p className="text-xs text-emerald-600 mt-1 font-medium">Promo applied: {verifiedPromo.code}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="special_requests">Special Requests</Label>
                  <textarea
                    id="special_requests"
                    data-testid="booking-special-requests"
                    value={bookingForm.special_requests}
                    onChange={(e) => setBookingForm({ ...bookingForm, special_requests: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                    rows={3}
                    placeholder="Any special requests?"
                  />
                </div>
              </div>

              <Button
                onClick={submitBooking}
                disabled={isBooking}
                data-testid="confirm-booking-btn"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12"
              >
                {isBooking 
                  ? 'Processing...' 
                  : (enablePaymentGateway ? 'Checkout' : 'Confirm Booking')
                }
              </Button>

              <p className="text-sm text-gray-500 text-center">
                {enablePaymentGateway 
                  ? 'Selesaikan pembayaran Anda dengan aman pada langkah selanjutnya' 
                  : 'You will be redirected to WhatsApp to complete payment'
                }
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Video Modal */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoUrl={selectedVideo.url}
        roomName={selectedVideo.name}
      />

      {/* Image Gallery Overlay */}
      <ImageGalleryOverlay
        images={galleryImages}
        initialIndex={galleryIndex}
        isOpen={showGallery}
        onClose={() => setShowGallery(false)}
        roomName={galleryRoomName}
      />

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Write a Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                    className="focus:outline-none transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 ${star <= reviewForm.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="rv_name">Name *</Label>
              <Input
                id="rv_name"
                value={reviewForm.guest_name}
                onChange={(e) => setReviewForm({ ...reviewForm, guest_name: e.target.value })}
                placeholder="Your name"
              />
            </div>
            <div>
              <Label htmlFor="rv_email">Email *</Label>
              <Input
                id="rv_email"
                type="email"
                value={reviewForm.guest_email}
                onChange={(e) => setReviewForm({ ...reviewForm, guest_email: e.target.value })}
                placeholder="your@email.com"
              />
            </div>
            <div>
              <Label htmlFor="rv_comment">Share your experience *</Label>
              <textarea
                id="rv_comment"
                value={reviewForm.comment}
                onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 min-h-[100px]"
                placeholder="Tell us about your stay..."
              />
            </div>
            <Button
              onClick={submitReview}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Submit Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Promo Overlay Modal */}
      <AnimatePresence>
        {showPromoOverlay && selectedPromoOffer && (() => {
          const mediaUrl = selectedPromoOffer.promo_media_url;
          const isVideo = mediaUrl.match(/\.(mp4|webm|ogg)$/i);
          const isYouTube = mediaUrl.includes('youtube.com') || mediaUrl.includes('youtu.be');

          let youtubeVideoId = '';
          if (isYouTube) {
            if (mediaUrl.includes('youtu.be/')) {
              youtubeVideoId = mediaUrl.split('youtu.be/')[1]?.split(/[?&#]/)[0];
            } else {
              const urlParams = new URLSearchParams(mediaUrl.split('?')[1]);
              youtubeVideoId = urlParams.get('v');
            }
          }

          return (
            <motion.div
              key="promo-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
              onClick={() => setShowPromoOverlay(false)}
            >
              {/* Close Button */}
              <button
                onClick={() => setShowPromoOverlay(false)}
                className="absolute top-4 right-4 z-[110] text-white/80 hover:text-white transition-colors bg-black/40 hover:bg-black/60 rounded-full p-2"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Media Content */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {isYouTube ? (
                  <div className="w-full aspect-video rounded-xl overflow-hidden shadow-2xl flex-shrink-0">
                    <iframe
                      src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0&modestbranding=1`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={selectedPromoOffer.title}
                      frameBorder="0"
                    />
                  </div>
                ) : isVideo ? (
                  <div className="w-full aspect-video rounded-xl overflow-hidden shadow-2xl flex-shrink-0">
                    <video
                      src={mediaUrl}
                      autoPlay
                      controls
                      playsInline
                      className="w-full h-full object-contain bg-black"
                    />
                  </div>
                ) : (
                  <div className="w-full flex items-center justify-center flex-shrink-0">
                    <img
                      src={mediaUrl}
                      alt={selectedPromoOffer.title}
                      className="max-w-full max-h-[55vh] object-contain rounded-xl shadow-2xl"
                    />
                  </div>
                )}

                {/* Offer Info at Bottom */}
                <div className="mt-3 text-center w-full flex-shrink-0 pb-2">
                  <h3 className="font-display text-lg sm:text-xl font-semibold text-white mb-1">{selectedPromoOffer.title}</h3>
                  <p className="text-white/70 text-sm mb-2 max-w-lg mx-auto line-clamp-2">{selectedPromoOffer.description}</p>
                  <div className="flex items-center justify-center gap-3">
                    <span className="bg-emerald-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                      Kode: {selectedPromoOffer.code}
                    </span>
                    <span className="text-white/50 text-xs">Valid until {selectedPromoOffer.validUntil}</span>
                  </div>
                  <Button
                    onClick={() => { setShowPromoOverlay(false); setShowSearchModal(true); }}
                    className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white px-8 rounded-full"
                  >
                    Cek Harga & Ketersediaan
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

    </div >
  );
};

export default Home;
